import {createSelector} from "reselect";
import {getCartItems} from "helpers/order-line.helper.ts";
import {getStartupParams} from "helpers/startup-params.helper";
import {getAvailablePaymentTypes, getCartTotal} from "helpers/purchase.helper.ts";
import {defaultCashpointsCacheEntry} from "reducers/cashpoints.reducer.ts";
import {defaultPurchaseCacheEntry} from "reducers/purchase.reducer.ts";
import {defaultServerValuesCacheEntry} from "reducers/server-values.reducer.ts";
import {defaultPromocodeCacheEntry} from "reducers/promocode.reducer.ts";
import {defaultTableNumberCacheEntry} from "reducers/table-number.reducer.ts";
import {OrderingTypeFilters} from "helpers/restaurant.helper";
import {OrderingTypes} from "new-constants/common.constants";
import {
	AdyenReturnModes,
	PaymentTypes,
	PopupPages,
	RoutePopupPages,
	TableNumberCacheLifespan
} from "../constants/common.constants";
import {getTipAmount} from "../helpers/purchase.helper";
import Platform from "../platform";
import Config from "../config";
import {history} from "../components/router/history"
import {getRouteByPathname} from "../helpers/routes.helper";
import {isActiveOrder, isHistoryOrder} from "../helpers/order.helper";

export const getAppStateSelector = state => state.appState;

export const getCurrentAppIdSelector = createSelector([getAppStateSelector], (appState) => {
	if (Platform.isNativeApp) {
		return Config.currentAppId;
	}

	return appState.appId || getStartupParams().app || (getStartupParams().id ? Config.defaultAppId : null);
});

export const getRestaurantListSelector = state => state.restaurantList;


const getDeliveryAddressSelector = state => state.deliveryAddress;
export const getOrderingModeSelector = state => state.orderingMode;

export const getCurrentDeliveryAddressSelector = createSelector(
	[getDeliveryAddressSelector, getOrderingModeSelector],
	(deliveryAddress, orderingMode) => {
		if(orderingMode?.orderingType === OrderingTypes.delivery){
			return deliveryAddress;
		}

		return null;
	}
)

export const getCurrentRestaurantListSelector = createSelector(
	[getRestaurantListSelector, getCurrentAppIdSelector],
	(restaurantList, currentAppId) => {
		return restaurantList.cache[currentAppId]?.restaurants
				.filter(restaurant => getStartupParams().id
					? restaurant.restaurantId === getStartupParams().id
					: true)
			|| [];
	});

export const getCurrentFilteredRestaurantListSelector = createSelector(
	[getOrderingModeSelector, getCurrentDeliveryAddressSelector, getRestaurantListSelector, getCurrentRestaurantListSelector],
	(orderingMode, currentDeliveryAddress, restaurantList, currentRestaurantList) => {
		if (!orderingMode) {
			return currentRestaurantList;
		}

		if (currentDeliveryAddress?.zip?.length > 0 &&
			currentDeliveryAddress?.zip === restaurantList.zipRestaurants.zip) {

			const zipRestaurantIds = restaurantList.zipRestaurants.restaurants.map(restaurant => restaurant.id);
			return currentRestaurantList
				.filter(restaurant => zipRestaurantIds.includes(restaurant.restaurantId))
				.map(restaurant => {
					const deliveryRestaurant = restaurantList.zipRestaurants.restaurants.find(deliveryRestaurant => deliveryRestaurant.id === restaurant.restaurantId);
					return {
						...restaurant,
						deliveryMinAmount: deliveryRestaurant.deliveryMinAmount,
						deliveryPrice: deliveryRestaurant.deliveryPrice
					}
				});
		} else {
			return currentRestaurantList
				.filter(OrderingTypeFilters[orderingMode.orderingType] || (() => true));
		}
	});

export const getCurrentRestaurantIdSelector = createSelector([
		getAppStateSelector,
		getCurrentFilteredRestaurantListSelector
	],
	(appState, currentFilteredRestaurantList) => {
		const isAppStateValid =
			currentFilteredRestaurantList.length <= 0 ||
			(currentFilteredRestaurantList.length > 0 &&
				currentFilteredRestaurantList.find(restaurant => restaurant.restaurantId === appState.restaurantId));

		return (
			(isAppStateValid && appState.restaurantId) ||
			getStartupParams().id ||
			(currentFilteredRestaurantList.length > 0 && currentFilteredRestaurantList[0].restaurantId)
		);
	});

export const getRestaurantCacheSelector = state => state.restaurant.cache;

export const getCurrentRestaurantSelector = createSelector(
	[getRestaurantCacheSelector, getCurrentRestaurantIdSelector],
	(restaurantCache, currentRestaurantId) => {
		return restaurantCache.find(cachedRestaurant => cachedRestaurant.restaurantId === currentRestaurantId);
	});
export const getCurrentCustomerSelector = state => state.customer.currentCustomer;

export const getCurrentGuestSelector = state => state.customer.currentGuest;

const getOrderLineCacheSelector = state => state.orderLine.cache;

export const getCurrentOrderLinesSelector = createSelector(
	[getOrderLineCacheSelector, getCurrentRestaurantIdSelector],
	(orderLineCache, currentRestaurantId) => orderLineCache[currentRestaurantId] || []
);

const getDefaultTakeAwayItemsSelector = state => state.orderLine.defaultTakeAwayItemsAdded;


export const getCurrentDefaultTakeAwayItemsAddedSelector = createSelector(
	[getDefaultTakeAwayItemsSelector, getCurrentRestaurantIdSelector],
	(defaultTakeAwayItems, currentRestaurantId) => defaultTakeAwayItems[currentRestaurantId] || []
);

const getOrderLineTemporaryCacheSelector = state => state.orderLine.temporaryCache;
export const getCurrentTemporaryOrderLinesSelector = createSelector(
	[getOrderLineTemporaryCacheSelector, getCurrentRestaurantIdSelector],
	(orderLineTemporaryCache, currentRestaurantId) => orderLineTemporaryCache[currentRestaurantId]
);

const getMenuCacheSelector = state => state.menu.cache;
export const getCurrentMenuSelector = createSelector(
	[getMenuCacheSelector, getCurrentRestaurantIdSelector],
	(menuCache, currentRestaurantId) => menuCache.find(menu => menu.restaurantId === currentRestaurantId)
)
export const getCurrentPurchaseSelector = state => state.purchase.cache[getCurrentRestaurantIdSelector(state)] || defaultPurchaseCacheEntry;

export const getOrderCacheSelector = state => state.order.cache;

export const getCurrentOrdersSelector = createSelector([
	getCurrentRestaurantListSelector, getOrderCacheSelector
], (currentRestaurantList, orderCache) => {
	const currentRestaurantIds = currentRestaurantList?.map(restaurant => restaurant.restaurantId) || [];

	return orderCache
		.reduce(
			(accum, restaurantOrders) =>
				currentRestaurantIds.includes(restaurantOrders.restaurantId)
					? [
						...accum,
						...restaurantOrders.orders
					]
					: accum,
			[]
		);
});

export const getCurrentActiveOrdersSelector = createSelector([
	getCurrentOrdersSelector
], (currentOrders) => {
	return currentOrders.filter(isActiveOrder);
});

export const getCurrentHistoryOrdersSelector = createSelector([
	getCurrentOrdersSelector
], (currentOrders) => {
	return currentOrders.filter(isHistoryOrder);
})

export const getCurrentCashpointsSelector = state => state.cashpoints.cache[getCurrentRestaurantIdSelector(state)] || defaultCashpointsCacheEntry;
export const getCurrentServerValuesSelector = state => state.serverValues.cache[getCurrentRestaurantIdSelector(state)] || defaultServerValuesCacheEntry;


export const getCurrentSessionTimeSelector = state => state.sessionTime.cache[getCurrentAppIdSelector(state)] || defaultSessionTimeCacheEntry;

export const getDeliveryConditionsSelector = createSelector(
	[getOrderingModeSelector, getRestaurantListSelector, getCurrentRestaurantIdSelector],
	(orderingMode, restaurantList, currentRestaurantId) => {
		if (orderingMode.orderingType !== OrderingTypes.delivery) return null;

		const deliveryRestaurant = restaurantList.zipRestaurants.restaurants.find(restaurant => restaurant.id === currentRestaurantId);

		return {
			price: deliveryRestaurant?.deliveryPrice || 0,
			minAmount: deliveryRestaurant?.deliveryMinAmount
		}
	});

export const getCurrentPromocodeSelector = state => state.promocode.cache[getCurrentRestaurantIdSelector(state)] || defaultPromocodeCacheEntry;
export const getCurrentTableNumberSelector = (state) => {

	const cachedTableEntry = state.tableNumber.cache[getCurrentRestaurantIdSelector(state)];


	if (cachedTableEntry?.timestamp && (Math.abs(Date.now() - cachedTableEntry.timestamp) < TableNumberCacheLifespan)) {
		return cachedTableEntry.table;
	}

	return getStartupParams().table || defaultTableNumberCacheEntry.table;
};


export const getCurrentServerConfig = state => state.serverConfig.cache[getCurrentAppIdSelector(state)];
export const getCurrentAuthSelector = state => state.auth.cache[getCurrentAppIdSelector(state)];
export const getTranslationsSelector = state => state.localization.translations || [];

export const getCartItemsSelector = createSelector([getCurrentOrderLinesSelector, getCurrentMenuSelector],
	getCartItems);

export const getAvailableOrderingTypesSelector = createSelector(
	[getCurrentRestaurantListSelector],
	(currentRestaurantList) => {
		return Object.keys(OrderingTypeFilters).filter(orderingType => currentRestaurantList.find(OrderingTypeFilters[orderingType]))
	});


export const isGiveTipsSelector = createSelector(
	[getCurrentMenuSelector, getCartItemsSelector],
	(currentMenu, cartItems) => {
		if (cartItems.length <= 0) return false;

		const cartItemMainCategoryIds = cartItems.map(cartItem => cartItem.mainCategoryId);

		return currentMenu.menu.menu
			.filter(mainCategory => mainCategory.giveTip && cartItemMainCategoryIds.includes(mainCategory.id))
			.length > 0
	}
)

export const getTipAmountSelector = createSelector(
	[isGiveTipsSelector, getCartItemsSelector, getCurrentPurchaseSelector, getCurrentServerValuesSelector, getDeliveryConditionsSelector,],
	(isGiveTips, cartItems, currentPurchase, currentServerValues, deliveryConditions) => {
		return isGiveTips ? getTipAmount(getCartTotal(currentPurchase, currentServerValues, deliveryConditions, cartItems, 0), currentPurchase?.tipPercent) : 0;
	}
)

export const getAvailablePaymentTypesSelector = createSelector(
	[
		getCurrentMenuSelector,
		getCurrentRestaurantSelector,
		getCartItemsSelector,
		getCurrentPurchaseSelector,
		getCurrentServerValuesSelector,
		getDeliveryConditionsSelector,
		getTipAmountSelector
	],
	(
		currentMenu,
		currentRestaurant,
		cartItems,
		currentPurchase,
		currentServerValues,
		deliveryConditions,
		tipAmount
	) =>
		getAvailablePaymentTypes(
			currentMenu,
			currentRestaurant?.paymentTypes,
			cartItems,
			currentServerValues?.discountPrice,
			currentPurchase?.tip,
			currentPurchase?.useCashPoints,
			currentServerValues?.cashpointsAmount,
			currentServerValues?.itemsWillBeRemoved
		)
);

export const getSubPaymentTypesSelector = createSelector(
	[getCurrentRestaurantSelector],
	(currentRestaurant) => currentRestaurant?.subPaymentTypes || []
);

export const getCurrentPaymentTypeSelector = createSelector(
	[getCurrentPurchaseSelector, getAvailablePaymentTypesSelector],
	(currentPurchase, availablePaymentTypes) => currentPurchase?.paymentType && availablePaymentTypes.includes(currentPurchase.paymentType)
		? currentPurchase.paymentType
		: (availablePaymentTypes.length > 0 && availablePaymentTypes[0])
);

export const getCurrentSubPaymentTypeSelector = createSelector(
	[getCurrentPaymentTypeSelector, getCurrentPurchaseSelector, getSubPaymentTypesSelector],
	(currentPaymentType, currentPurchase, subPaymentTypes) => currentPaymentType === PaymentTypes.customPaymentType
		? subPaymentTypes.find(subPaymentType => subPaymentType.id === currentPurchase.subPaymentType) : null
);

export const getRoutingSelector = state => state.routing;

export const getCurrentServerPopupsSelector = createSelector(
	[getCurrentCustomerSelector, getRoutingSelector],
	(
		currentCustomer,
		routing,
		// Added to refresh selector on page change
	) => {
		return currentCustomer?.popupsForPages?.filter(p => p.showOnPage === RoutePopupPages[getRouteByPathname(history.location?.pathname)]) || [];
	}
);

export const getCurrentCustomerIdSelector = createSelector(
	[getCurrentCustomerSelector],
	(currentCustomer) => {
		return currentCustomer?.customerId;
	}
)

export const getCurrentTemporaryOrderIdSelector = state => state.payment.orderId;

export const getCurrentTemporaryOrderSelector = createSelector(
	[getCurrentTemporaryOrderIdSelector, getOrderCacheSelector],
	(currentTemporaryOrderId, orderCache) => {
		return orderCache.reduce((accum, restaurantOrders) => accum || restaurantOrders.orders.find(order => order.orderId === currentTemporaryOrderId), null);
	}
)

export const getAdyenRedirectResultSelector = state => state.payment.adyenRedirectResult;
export const getAdyenRedirectResultSecret = state => state.payment.adyenRedirectResultSecret;
export const getAdyenReturnOrderIdSelector = state => state.adyenReturn.adyenReturnOrderId;


export const getAdyenReturnModeSelector = createSelector(
	[getAdyenReturnOrderIdSelector],
	(adyenReturnOrderId) => {
		if (!adyenReturnOrderId && !getStartupParams().orderId)  {
			return null;
		} else if (!!adyenReturnOrderId && !getStartupParams().orderId) {
			return AdyenReturnModes.URL_IS_NULL;
		} else if (adyenReturnOrderId === getStartupParams().orderId) {
			return AdyenReturnModes.URL_STATE_MATCH;
		} else {
			return AdyenReturnModes.URL_STATE_MISMATCH;
		}
	}
)
